import { Tile } from '@carbon/react';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { ListTable, type ListColumn } from '../components/ListTable';
import { PageHeader } from '../components/PageHeader';
import { StatusTag } from '../components/StatusTag';
import { FilterLayout } from '../filters/FilterPanel';
import { useFilters } from '../filters/useFilters';
import { asProblem, problemMessage } from '../problems';
import { dateRange, ordersKey, today, useFormat, useOrderSetup, useOrdersApi, type OrdersMode } from './model';
import { DateStoreFilters, FilterDropdown, StoreName, type DateStoreValue } from './TransactionFilters';
import './orders.scss';

const PAGE_SIZES = [25, 50, 100];
const NO_PROVIDER = 'none';
const KINDS = ['payment', 'refund'] as const;
const STATUSES = ['approved', 'pending', 'declined', 'voided', 'failed'] as const;

/**
 * Every payment and refund across orders with the provider that carried it: cash and own tenders, card acquirers,
 * QR and e-wallet providers (ADR 0010). Totals per provider cover all pages of the filtered list.
 */
export function PaymentTransactionsPage({ mode }: { mode: OrdersMode }) {
  const { t } = useTranslation();
  const api = useOrdersApi();
  const navigate = useNavigate();
  const format = useFormat();
  const { setup, paymentMethodName } = useOrderSetup();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const filters = useFilters(
    { from: today(), to: today(), storeId: '', provider: '', methodType: '', kind: '', status: '' },
    () => setPage(1),
  );
  const { provider, methodType, kind, status } = filters.applied;
  const range: DateStoreValue = {
    from: filters.applied.from,
    to: filters.applied.to,
    storeId: filters.applied.storeId,
  };
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0] ?? 25);
  const providers = useQuery({ queryKey: [...ordersKey, 'payment-providers'], queryFn: () => api.paymentProviders() });

  const query = {
    ...dateRange(range.from, range.to),
    storeId: range.storeId || undefined,
    provider: provider || undefined,
    methodType: methodType || undefined,
    kind: kind || undefined,
    status: status || undefined,
    search: search || undefined,
    page,
    pageSize,
  };
  const list = useQuery({
    queryKey: [...ordersKey, 'payment-transactions', query],
    queryFn: () => api.paymentTransactions(query),
    placeholderData: keepPreviousData,
  });
  const reset =
    <T,>(set: (v: T) => void) =>
    (v: T) => {
      set(v);
      setPage(1);
    };

  // Providers from payments so far, plus those behind the payment methods set up at HO.
  const providerOptions = new Map<string, string>();
  for (const p of providers.data ?? []) providerOptions.set(p.code, p.name);
  for (const m of setup?.paymentMethods ?? [])
    if (m.providerCode) providerOptions.set(m.providerCode, m.providerName ?? m.providerCode);
  const methodTypes = [...new Set((setup?.paymentMethods ?? []).map((m) => m.methodType))];
  const typeLabel = (type: string) => t(`orders.methodTypes.${type}` as I18nKey, { defaultValue: type });

  type Item = NonNullable<typeof list.data>['items'][number];
  const columns: ListColumn<Item & { id: string }>[] = [
    {
      key: 'paidAt',
      header: t('transactions.payments.columns.paidAt'),
      sortable: true,
      sortValue: (i) => i.payment.paidAt,
      render: (i) => format.dateTime(i.payment.paidAt),
    },
    {
      key: 'order',
      header: t('transactions.payments.columns.order'),
      render: (i) => <Link to={`/orders/${i.payment.orderId}`}>{i.orderNumber ?? i.payment.orderId.slice(0, 8)}</Link>,
    },
    ...(mode === 'ho'
      ? [
          {
            key: 'store',
            header: t('transactions.payments.columns.store'),
            render: (i: Item) => <StoreName id={i.payment.storeId} />,
          },
        ]
      : []),
    { key: 'provider', header: t('transactions.payments.columns.provider'), render: (i) => i.payment.providerName ?? '' },
    {
      key: 'method',
      header: t('transactions.payments.columns.method'),
      render: (i) => paymentMethodName(i.payment.paymentMethodId) ?? typeLabel(i.payment.methodType),
    },
    {
      key: 'kind',
      header: t('transactions.payments.columns.kind'),
      render: (i) => (
        <StatusTag status={i.payment.kind}>
          {t(`orders.payments.kinds.${i.payment.kind}` as I18nKey, { defaultValue: i.payment.kind })}
        </StatusTag>
      ),
    },
    {
      key: 'amount',
      header: t('transactions.payments.columns.amount'),
      sortable: true,
      sortValue: (i) => Number(i.payment.amount),
      className: 'pos-admin-page__number',
      render: (i) => (
        <>
          {i.payment.kind === 'refund' ? '−' : ''}
          {format.money(i.payment.amount, i.payment.currencyCode)}
        </>
      ),
    },
    {
      key: 'reference',
      header: t('transactions.payments.columns.reference'),
      render: ({ payment: p }) =>
        [
          p.providerReference,
          p.qrReference,
          p.cardLast4 && `${p.cardBrand ?? ''} •••• ${p.cardLast4}`.trim(),
        ]
          .filter(Boolean)
          .join(' · '),
    },
    {
      key: 'status',
      header: t('transactions.payments.columns.status'),
      render: (i) => (
        <StatusTag status={i.payment.status}>
          {t(`transactions.payments.statuses.${i.payment.status}` as I18nKey, { defaultValue: i.payment.status })}
        </StatusTag>
      ),
    },
    {
      key: 'confirmation',
      header: t('transactions.payments.columns.confirmation'),
      render: (i) =>
        t(`transactions.payments.confirmation.${i.payment.confirmationMode}` as I18nKey, {
          defaultValue: i.payment.confirmationMode,
        }),
    },
    { key: 'receivedBy', header: t('transactions.payments.columns.receivedBy'), render: (i) => i.payment.receivedByName ?? '' },
  ];
  const items = (list.data?.items ?? []).map((i) => ({ ...i, id: i.payment.id }));

  return (
    <section className="pos-admin-page">
      <PageHeader title={t('transactions.payments.title')} />
      <FilterLayout
        filters={filters}
        fields={
          <>
            <DateStoreFilters
              idPrefix="payments"
              mode={mode}
              value={{ from: filters.draft.from, to: filters.draft.to, storeId: filters.draft.storeId }}
              onChange={(v) => filters.setDraft((d) => ({ ...d, ...v }))}
            />
            <FilterDropdown
              id="payments-provider"
              label={t('transactions.filters.provider')}
              options={[
                { id: NO_PROVIDER, label: t('transactions.filters.noProvider') },
                ...[...providerOptions].map(([id, label]) => ({ id, label })),
              ]}
              value={filters.draft.provider}
              onChange={(v) => filters.set('provider', v)}
            />
            <FilterDropdown
              id="payments-type"
              label={t('transactions.filters.methodType')}
              options={methodTypes.map((m) => ({ id: m, label: typeLabel(m) }))}
              value={filters.draft.methodType}
              onChange={(v) => filters.set('methodType', v)}
            />
            <FilterDropdown
              id="payments-kind"
              label={t('transactions.filters.kind')}
              options={KINDS.map((k) => ({ id: k, label: t(`orders.payments.kinds.${k}`) }))}
              value={filters.draft.kind}
              onChange={(v) => filters.set('kind', v)}
            />
            <FilterDropdown
              id="payments-status"
              label={t('transactions.filters.status')}
              options={STATUSES.map((s) => ({ id: s, label: t(`transactions.payments.statuses.${s}`) }))}
              value={filters.draft.status}
              onChange={(v) => filters.set('status', v)}
            />
          </>
        }
      >
        {(list.data?.summary.length ?? 0) > 0 && (
          <div className="pos-transactions__summary">
            {list.data?.summary.map((s) => (
              <Tile
                key={`${s.providerCode ?? NO_PROVIDER}-${s.methodType}-${s.currencyCode}`}
                className="pos-transactions__tile"
              >
                <p className="pos-transactions__tile-title">{s.providerName ?? t('transactions.filters.noProvider')}</p>
                <p className="pos-orders__muted">
                  {typeLabel(s.methodType)} · {t('transactions.payments.summary.count', { count: Number(s.count) })}
                </p>
                <dl className="pos-transactions__tile-figures">
                  <dt>{t('transactions.payments.summary.paid')}</dt>
                  <dd className="pos-admin-page__number">{format.money(s.paid, s.currencyCode)}</dd>
                  {Number(s.refunded) > 0 && (
                    <>
                      <dt>{t('transactions.payments.summary.refunded')}</dt>
                      <dd className="pos-admin-page__number">−{format.money(s.refunded, s.currencyCode)}</dd>
                    </>
                  )}
                </dl>
              </Tile>
            ))}
          </div>
        )}

        <ListTable
          ariaLabel={t('transactions.payments.title')}
          columns={columns}
          rows={items}
          loading={list.isLoading}
          error={list.error ? problemMessage(t, asProblem(list.error)) : undefined}
          onRetry={() => void list.refetch()}
          search={{ label: t('transactions.payments.search'), onChange: reset(setSearch) }}
          filters={filters}
          onRowClick={(i) => void navigate(`/sales/payments/${i.payment.id}`)}
          empty={{ heading: t('transactions.payments.empty') }}
          pagination={{
            page,
            pageSize,
            pageSizes: PAGE_SIZES,
            total: list.data?.total ?? 0,
            onChange: ({ page: next, pageSize: size }) => {
              setPage(next);
              setPageSize(size);
            },
          }}
        />
      </FilterLayout>
    </section>
  );
}
